import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { FounderIcon } from "@/components/pricing/founderIcons";
import DonateCta from "@/components/pricing/DonateCta";

const money = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

// "venture_otter_founder" -> "venture" (for stable, design-mapped test ids)
const slugOf = (key) => (key || "").split("_")[0] || "founder";

function useCheckout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingKey, setPendingKey] = useState(null);

  const buy = async (tier) => {
    if (!user) {
      navigate("/login?next=/pricing");
      return;
    }
    setPendingKey(tier.key);
    try {
      const { data } = await api.post("/founders/checkout", {
        founder_key: tier.key,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      const code = e?.response?.data?.code;
      if (e?.response?.status === 403 && code === "EMAIL_NOT_VERIFIED") {
        toast.error("Please verify your email before purchasing.");
        navigate("/verify-email");
      } else {
        toast.error(e?.response?.data?.detail || "Could not start checkout.");
      }
      setPendingKey(null);
    }
  };
  return { buy, pendingKey };
}

function FeatureItem({ text }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#148b8f]" />
      <span className="text-sm leading-relaxed text-slate-300">{text}</span>
    </li>
  );
}

function VentureHero({ tier, buy, pending }) {
  const slug = slugOf(tier.key);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative grid grid-cols-1 gap-8 rounded-none border border-[#148b8f]/40 bg-[rgba(20,139,143,0.06)] p-8 shadow-[0_0_80px_rgba(20,139,143,0.10)] backdrop-blur-2xl lg:grid-cols-5 lg:gap-12 lg:p-12"
      data-testid={`${slug}-founder-card`}
    >
      <span className="absolute -top-6 left-8 flex h-12 w-12 items-center justify-center rounded-full bg-[#148b8f] text-white shadow-[0_0_24px_rgba(20,139,143,0.5)] lg:left-12">
        <FounderIcon name={tier.icon} className="h-6 w-6" />
      </span>

      <div className="flex flex-col justify-between lg:col-span-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2dd4bf]">Most exclusive</p>
          <h3 className="mt-3 text-2xl font-normal tracking-tight text-white lg:text-3xl">{tier.name}</h3>
          {tier.tagline && <p className="mt-2 text-sm leading-relaxed text-slate-400">{tier.tagline}</p>}
          {tier.available > 0 && (
            <p className="mt-3 text-sm font-[640] text-[#2dd4bf]" data-testid={`${slug}-founder-available`}>
              Only {tier.available.toLocaleString("en-US")} available
            </p>
          )}
          <div className="mt-6 flex items-baseline gap-2">
            <span className="text-4xl font-[760] text-white lg:text-5xl">{money(tier.price)}</span>
            <span className="text-sm text-slate-400">one-time</span>
          </div>
          {tier.duration_label && (
            <p className="mt-1 text-sm text-[#2dd4bf]">{tier.duration_label}</p>
          )}
        </div>
        <button
          onClick={() => buy(tier)}
          disabled={pending}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-none border border-transparent bg-[#148b8f] py-4 text-base font-[640] text-white transition-colors hover:bg-[#107073] disabled:opacity-60"
          data-testid={`${slug}-founder-checkout-button`}
        >
          {pending ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          Become a {tier.name.replace(" Otter Founder", "")} Founder
        </button>
      </div>

      <div className="lg:col-span-3">
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {(tier.features || []).map((f, i) => <FeatureItem key={f.id || i} text={f.name} />)}
        </ul>
        {tier.footnote && tier.footnote !== "<p></p>" && (
          <div className="mt-6 border-t border-white/10 pt-4 text-xs text-slate-500 [&_a]:text-[#2dd4bf]">
            <RichTextView html={tier.footnote} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

function StandardCard({ tier, buy, pending, index }) {
  const slug = slugOf(tier.key);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 * index }}
      className="relative flex flex-col rounded-none border border-white/10 bg-white/[0.03] p-8 pt-12 backdrop-blur-xl transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-white/25"
      data-testid={`${slug}-founder-card`}
    >
      <span className="absolute -top-6 left-8 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-slate-900 text-[#2dd4bf]">
        <FounderIcon name={tier.icon} className="h-6 w-6" />
      </span>

      <h3 className="text-xl font-normal tracking-tight text-white">{tier.name}</h3>
      {tier.tagline && <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{tier.tagline}</p>}
      {tier.available > 0 && (
        <p className="mt-2.5 text-sm font-[640] text-[#2dd4bf]" data-testid={`${slug}-founder-available`}>
          Only {tier.available.toLocaleString("en-US")} available
        </p>
      )}
      <div className="mt-5 flex items-baseline gap-2">
        <span className="text-3xl font-[760] text-white">{money(tier.price)}</span>
        <span className="text-sm text-slate-400">one-time</span>
      </div>
      {tier.duration_label && <p className="mt-1 text-xs text-[#2dd4bf]">{tier.duration_label}</p>}

      <ul className="mt-7 flex flex-1 flex-col gap-3.5">
        {(tier.features || []).map((f, i) => <FeatureItem key={f.id || i} text={f.name} />)}
      </ul>

      {tier.footnote && tier.footnote !== "<p></p>" && (
        <div className="mt-5 border-t border-white/10 pt-4 text-xs text-slate-500 [&_a]:text-[#2dd4bf]">
          <RichTextView html={tier.footnote} />
        </div>
      )}

      <button
        onClick={() => buy(tier)}
        disabled={pending}
        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-none border border-white/20 bg-transparent py-4 font-[640] text-white transition-all hover:border-[#148b8f] hover:bg-[#148b8f]/10 disabled:opacity-60"
        data-testid={`${slug}-founder-checkout-button`}
      >
        {pending ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        Become a Founder
      </button>
    </motion.div>
  );
}

export default function FoundersSection() {
  const { buy, pendingKey } = useCheckout();
  const { data } = useQuery({
    queryKey: ["founders"],
    queryFn: async () => (await api.get("/founders")).data,
  });

  const tiers = data?.tiers || [];
  const settings = data?.settings || {};
  if (tiers.length === 0) {
    // Founders cards hidden (sold out / past the hide-after date) — donations still stand alone.
    return (
      <section className="relative overflow-hidden bg-[#040d12] px-6 py-16 sm:px-10" data-testid="donate-only-section">
        <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-[#148b8f]/10 blur-3xl" />
        <div className="relative mx-auto max-w-6xl"><DonateCta /></div>
      </section>
    );
  }
  const hero = tiers.find((t) => t.highlighted) || tiers[0];
  const rest = tiers.filter((t) => t.key !== hero.key);

  return (
    <section
      className="relative overflow-hidden rounded-none bg-[#040d12] px-6 py-20 sm:px-10 lg:py-24"
      data-testid="founders-membership-section"
    >
      {/* ambient glow */}
      <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-[#148b8f]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-[#148b8f]/10 blur-3xl" />

      <div className="relative mx-auto flex max-w-6xl flex-col gap-14">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2dd4bf]">Limited opportunity</p>
          <h2 className="text-4xl font-light tracking-tight text-white sm:text-5xl">{settings.title || "Founders Membership"}</h2>
          {settings.intro && <p className="text-base leading-relaxed text-slate-400">{settings.intro}</p>}
        </div>

        <div className="flex flex-col gap-8">
          <VentureHero tier={hero} buy={buy} pending={pendingKey === hero.key} />
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {rest.map((t, i) => (
              <StandardCard key={t.id} tier={t} buy={buy} pending={pendingKey === t.key} index={i} />
            ))}
          </div>
          <DonateCta />
        </div>
      </div>
    </section>
  );
}
